import { useCallback } from 'react'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useTRPC } from '@lib/trpc'

type Scope = { teamId: string; envId: string }

const EMPTY: Scope = { teamId: '', envId: '' }

export const useOrganizations = (scope: Scope | undefined) => {
  const trpc = useTRPC()
  const query = useQuery({ ...trpc.organizations.list.queryOptions(scope ?? EMPTY), enabled: Boolean(scope) })
  return { organizations: query.data ?? [], organizationsLoading: query.isPending }
}

export const useOrganization = (scope: Scope | undefined, tenant: string | undefined) => {
  const trpc = useTRPC()
  const query = useQuery({
    ...trpc.organizations.get.queryOptions({ ...(scope ?? EMPTY), tenant: tenant ?? '' }),
    enabled: Boolean(scope && tenant)
  })
  return { organization: query.data, organizationLoading: query.isPending }
}

export const useCompanySuggestion = (scope: Scope | undefined, domain: string) => {
  const trpc = useTRPC()
  const query = useQuery({
    ...trpc.organizations.suggestCompany.queryOptions({ ...(scope ?? EMPTY), domain }),
    enabled: Boolean(scope && domain),
    staleTime: Infinity
  })
  return { company: query.data ?? null }
}

export const usePrefetchOrganization = () => {
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  return useCallback(
    (scope: Scope | undefined, tenant: string) => {
      if (!scope) return
      queryClient.prefetchQuery({ ...trpc.organizations.get.queryOptions({ ...scope, tenant }), staleTime: 30_000 })
    },
    [queryClient, trpc]
  )
}

const useInvalidateOrganizations = () => {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: trpc.organizations.list.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.organizations.get.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.setup.get.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.overview.summary.queryKey() })
    ])
}

export const useCreateOrganizationMutation = () => {
  const trpc = useTRPC()
  const invalidate = useInvalidateOrganizations()
  const mutation = useMutation(trpc.organizations.create.mutationOptions({ onSuccess: invalidate }))
  return { createOrganization: mutation.mutateAsync, createOrganizationIsPending: mutation.isPending }
}

export const useImportOrganizationsCsvMutation = () => {
  const trpc = useTRPC()
  const invalidate = useInvalidateOrganizations()
  const mutation = useMutation(trpc.organizations.importCsv.mutationOptions({ onSuccess: invalidate }))
  return { importOrganizationsCsv: mutation.mutateAsync, importOrganizationsCsvIsPending: mutation.isPending }
}

export const useUpdateOrganizationMutation = () => {
  const trpc = useTRPC()
  const invalidate = useInvalidateOrganizations()
  const mutation = useMutation(trpc.organizations.update.mutationOptions({ onSuccess: invalidate }))
  return { updateOrganization: mutation.mutateAsync, updateOrganizationIsPending: mutation.isPending }
}

export const useDeleteOrganizationMutation = () => {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const invalidate = useInvalidateOrganizations()
  const mutation = useMutation(
    trpc.organizations.delete.mutationOptions({
      onSuccess: () =>
        Promise.all([
          invalidate(),
          queryClient.invalidateQueries({ queryKey: trpc.sso.list.queryKey() }),
          queryClient.invalidateQueries({ queryKey: trpc.dsync.list.queryKey() }),
          queryClient.invalidateQueries({ queryKey: trpc.portalLinks.list.queryKey() }),
          queryClient.invalidateQueries({ queryKey: trpc.billing.status.queryKey() })
        ])
    })
  )
  return { deleteOrganization: mutation.mutateAsync, deleteOrganizationIsPending: mutation.isPending }
}
